import { Fragment } from "react";

import Link from "@whoppah/next/link";
import { Trans, useTranslation } from "next-i18next";

import GoogleReviewsPreval from "scenes/auth/register/preval/GoogleReviews.preval";

import { ChevronDown } from "@/assets/icons/24/ChevronDown";
import { AppDownloadButton } from "@/common/components/AppDownloadButton";
import { GoogleReviewRating } from "@/common/components/GoogleReviewRating";
import { PaymentMethods } from "@/common/components/PaymentMethods";
import { AWARDS, LINKS_MAP } from "@/common/static/footer.static";
import { PAYMENT_ROUTE } from "@/common/static/routes.static";
import { cn } from "@/common/utils/css.utils";
import { FooterLink } from "@/components/Footer/FooterLink";
import { FooterNewsletter } from "@/components/Footer/FooterNewsletter";
import { FooterWhoppahInfo } from "@/components/Footer/FooterWhoppahInfo";
import Image from "@/components/common/Image";
import { useContentLocale } from "@/i18n/hooks";
import { Disclosure } from "@headlessui/react";
import { Heading } from "@whoppah/ui/cva/Heading";
import { Body } from "@whoppah/ui/typography/Body";

type FooterProps = {
  backgroundColor?: string;
  className?: string;
};

export const Footer = ({ backgroundColor, className }: FooterProps) => {
  const { t } = useTranslation();
  const locale = useContentLocale();

  const { rating } = GoogleReviewsPreval[locale];

  return (
    <footer
      style={{ backgroundColor: backgroundColor }}
      className={cn(
        "pb-[74px] md:pb-0",
        backgroundColor ? `bg-${backgroundColor}` : "bg-[#F0F5F0]",
        className
      )}
    >
      <div className="px-6 py-6 lg:px-12 xl:px-24">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-[auto_1fr]">
          <div className="flex flex-col space-y-8 md:max-w-80">
            <FooterWhoppahInfo />
            <FooterNewsletter className="hidden md:flex" />
          </div>
          {/*
           * One copy of the link groups for both breakpoints. They used to be
           * rendered twice, as a four-column grid for md+ and as accordions
           * for mobile, which put every footer link in the HTML twice (12 KB
           * a page). Now each group is one Disclosure: its heading-only title
           * shows on md+, its button on mobile, and its panel is `static` so
           * the links are always in the document; on mobile the panel toggles,
           * on md+ it is simply visible. The negative margins pull the mobile
           * stack out of the wrapper's padding so the separators run
           * edge to edge, as the old stand-alone accordions did.
           * Columns are minmax(0,1fr), not 1fr: a bare 1fr has an auto minimum,
           * so one long compound word ("Gebruiksvoorwaarden") widened its
           * column and overflowed the page at 768px. Words now hyphenate.
           */}
          <div className="-mx-6 -mb-6 -mt-2 md:m-0 md:grid md:grid-cols-[repeat(4,minmax(0,1fr))] md:gap-8">
            <hr className="border border-t-[2px] border-[#C0D7CA]/[.4] md:hidden" />
            {LINKS_MAP.map(value => (
              <Fragment key={value.title}>
                <Disclosure
                  as="div"
                  className="px-6 md:flex md:flex-col md:space-y-3 md:px-0"
                >
                  {({ open }) => (
                    <>
                      <Heading
                        as="h5"
                        className="hidden hyphens-auto break-words text-brand-500 md:block"
                      >
                        {t(value.title)}
                      </Heading>
                      <Disclosure.Button className="flex w-full items-center gap-x-2 py-4 text-left focus:outline-none focus-visible:ring focus-visible:ring-opacity-75 md:hidden">
                        <Heading as="h5" size="h4" className="text-brand-500">
                          {t(value.title)}
                        </Heading>
                        <ChevronDown
                          className={cn(
                            "block h-6 w-6 shrink-0 text-brand-500",
                            open ? "rotate-180 transform" : ""
                          )}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel
                        static
                        className={cn(
                          "flex-col gap-y-4 hyphens-auto break-words pb-6 font-body text-sm md:flex md:gap-y-3 md:pb-0",
                          open ? "flex" : "hidden"
                        )}
                      >
                        {value.links.map(link => (
                          <FooterLink key={link.label} link={link} />
                        ))}
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
                <hr className="border border-t-[2px] border-[#C0D7CA]/[.4] md:hidden" />
              </Fragment>
            ))}
          </div>
        </div>
      </div>
      <hr className="hidden border border-t-[2px] border-[#C0D7CA]/[.4] md:block" />
      <div className="grid grid-cols-2 items-center gap-y-4 px-6 py-6 md:flex md:gap-x-14 lg:px-12 xl:px-24">
        {AWARDS.map(award => (
          <div
            key={award.title}
            className="w-min-full relative h-12 md:w-[160px]"
          >
            <Image
              src={award.image}
              alt={award.title + " image"}
              layout="fill"
              objectFit="contain"
            />
          </div>
        ))}
      </div>
      <hr className="border border-t-[2px] border-[#C0D7CA]/[.4] md:hidden" />
      <div className="px-6 py-6 md:hidden lg:px-12 xl:px-24">
        <FooterNewsletter />
      </div>
      <hr className="border border-t-[2px] border-[#C0D7CA]/[.4]" />
      <div className="flex flex-col-reverse justify-between gap-y-4 px-6 py-10 md:flex-row md:items-center lg:px-12 xl:px-24">
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:space-x-4 md:space-y-0">
          <div className="flex justify-center space-x-2 md:-ml-2 md:justify-start">
            <AppDownloadButton
              device="ios"
              className="h-[40px] w-[124px] min-[380px]:h-[45px] min-[380px]:w-[140px] min-[768px]:h-[30px] min-[768px]:w-[100px]"
            />
            <AppDownloadButton
              device="android"
              className="h-[40px] w-[124px] min-[380px]:h-[45px] min-[380px]:w-[140px] min-[768px]:h-[30px] min-[768px]:w-[100px]"
            />
          </div>
          <div className="w-min-full md:w-min-auto block md:h-8">
            <Link
              href={PAYMENT_ROUTE}
              aria-label={t("footer:footer-payment")}
              className="flex flex-col space-y-2 focus:rounded-[0.25rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-curious-200 focus-visible:ring-offset-4 focus-visible:ring-offset-[#F0F5F0] md:mt-[2px] md:flex-row md:space-x-2 md:space-y-0"
            >
              <PaymentMethods size="large" />
            </Link>
          </div>
          <p className="text-center font-body text-[14px] font-medium text-[#B0B0B0] md:hidden">
            {t("footer:footer-rights-reserved")} &middot; ©{" "}
            {new Date().getFullYear()} Whoppah
          </p>
        </div>
        <div className="flex flex-col gap-y-2 md:hidden lg:block lg:px-3">
          <GoogleReviewRating size="large" />
          {/*
           * ponytail: the translation is plain HTML (`<strong>{{rating}}</strong>`),
           * not markdown — Trans keeps <strong> via transKeepBasicHtmlNodesFor and
           * reuses react-i18next, which is already in the bundle. Importing
           * markdown-to-jsx here put its ~102 KB parser in the shared _app chunk
           * (Footer is in MainLayout) on every page. Use LokaliseMarkdownText if a
           * real markdown key ever lands here.
           */}
          <Body as="p">
            <Trans
              i18nKey="footer:google-reviews-text-footer"
              values={{ rating }}
            />
          </Body>
        </div>
      </div>
      <hr className="hidden border border-t-[2px] border-[#C0D7CA]/[.4] md:block" />
      <div className="hidden px-6 py-6 md:block lg:px-12 xl:px-24">
        <p className="font-body text-sm font-medium text-[#B0B0B0]">
          {t("footer:footer-rights-reserved")} &middot; ©{" "}
          {new Date().getFullYear()} Whoppah
        </p>
      </div>
    </footer>
  );
};
